import * as React from 'react'
import { tw } from 'utils/classnames'
import { useLocale } from 'next-intl'
import type { Language } from 'codegen/types'
import { useLangCookie } from 'cf-i18n/cookies'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { useSiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import * as Sentry from '@sentry/nextjs'
import { getCookie } from 'utils/dom'
import {
  SUBSCRIPTION_STATUS_COOKIE_NAME,
  USER_GQL_LOGGED_STATUS_COOKIE_NAME,
  USER_ID_COOKIE_NAME,
  WORDPRESS_LOGIN_COOKIE_NAME,
} from 'constants/cookies'
import { useUserQuery } from 'auth/hooks'
import { AMPLITUDE_DEVICE_ID_COOKIE_NAME } from '@repo/analytics/amplitude'
import { transformImage } from '../../utils/image'

interface Props {
  className?: string
  wrapperClassName?: string
  backgroundImage?: string
  backgroundColor?: string
  bannerType: string
}
export function BannerContainer({
  backgroundImage,
  backgroundColor,
  children,
  className,
  wrapperClassName,
  bannerType,
}: React.PropsWithChildren<Props>) {
  const {
    data: {
      isAnonymous,
      isEligibleForAllAccessFreeTrial,
      isEligibleForAllAccessYearlyUpgrade,
      userHasAllAccess,
    } = {},
    isPending: userIsLoading,
  } = useUserQuery(undefined, {
    select: data => ({
      isAnonymous: data?.me?.user?.isTemporary,
      isEligibleForAllAccessFreeTrial:
        data?.me?.user?.subscriptionAccess.allAccess.isEligibleForFreeTrial,
      isEligibleForAllAccessYearlyUpgrade:
        data?.me?.user?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade,
      userHasAllAccess: data?.me?.user?.subscriptionAccess?.allAccess.isSubscribed || false,
    }),
  })
  const [langCookie, setLangCookie] = useLangCookie()
  const { data: siteDataQuery, isLoading: siteDataIsLoading } = useSiteDataQuery()
  const locale = useLocale() as Language

  const wordpressBannerType = getWordpressBannerType(siteDataQuery?.siteData)

  React.useEffect(() => {
    if (!langCookie && locale && locale !== langCookie) {
      setLangCookie(locale)
    }
  }, [locale, langCookie, setLangCookie])

  React.useEffect(() => {
    sendAmplitudeEvent(AmplitudeEvents.BANNER_VIEWED, {
      bannerType,
    })
  }, [bannerType])

  React.useEffect(() => {
    if (siteDataIsLoading || !wordpressBannerType || userIsLoading) {
      return
    }

    if (wordpressBannerType !== bannerType) {
      const wpCookieIsLoggedIn = !!getCookie(WORDPRESS_LOGIN_COOKIE_NAME)

      // This is a known case where we do not want to send an error to Sentry
      // because WP is showing the wrong banner for logged out users with the promo cookie
      if (bannerType === 'all_access_yearly_trial' && !wpCookieIsLoggedIn) {
        return
      }

      Sentry.withScope(scope => {
        const deviceId = getCookie(AMPLITUDE_DEVICE_ID_COOKIE_NAME)
        const userId = getCookie(USER_ID_COOKIE_NAME)
        const gqlCookieIsLoggedIn = getCookie(USER_GQL_LOGGED_STATUS_COOKIE_NAME)

        // oxlint-disable-next-line no-negated-condition
        if (!isAnonymous) {
          scope.setUser({
            id: userId,
            deviceId,
            isAnonymous: false,
            isEligibleForAllAccessFreeTrial: isEligibleForAllAccessFreeTrial,
          })
        } else {
          scope.setUser({
            deviceId,
            isAnonymous: true,
          })
        }

        // Set custom tags for queryable segmentation
        scope.setTag('banner.static_variant', bannerType)
        scope.setTag('banner.wordpress_type', wordpressBannerType || 'unknown')
        scope.setTag('user.wp_logged_in', wpCookieIsLoggedIn ? 'true' : 'false')
        scope.setTag('user.gql_logged_in', gqlCookieIsLoggedIn ? 'true' : 'false')
        scope.setTag('user.is_anonymous', isAnonymous ? 'true' : 'false')
        scope.setTag('user.eligible_free_trial', isEligibleForAllAccessFreeTrial ? 'true' : 'false')
        scope.setTag(
          'user.eligible_yearly_upgrade',
          isEligibleForAllAccessYearlyUpgrade ? 'true' : 'false',
        )
        scope.setTag('user.all_access', userHasAllAccess ? 'true' : 'false')
        scope.setTag('user.subscription', getCookie(SUBSCRIPTION_STATUS_COOKIE_NAME) || 'unknown')
        if (deviceId) {
          scope.setTag('device.amplitude_id', deviceId)
        }

        // Keep additional context as extra data for detailed analysis
        scope.setExtra('mismatch_details', {
          staticVariantBannerType: bannerType,
          wordpressBannerType,
          timestamp: new Date().toISOString(),
        })

        // Sentry.captureException(new Error('Static variant banner does NOT match WP banner'))
      })
    }
  }, [
    wordpressBannerType,
    siteDataIsLoading,
    bannerType,
    isAnonymous,
    isEligibleForAllAccessFreeTrial,
    isEligibleForAllAccessYearlyUpgrade,
    userIsLoading,
    userHasAllAccess,
  ])

  const style = {
    backgroundColor,
  }

  return (
    <div
      className={tw(
        'relative flex flex-col justify-between gap-5 overflow-hidden bg-cover bg-no-repeat text-[14px]',
        className,
      )}
      style={style}
    >
      {backgroundImage ? (
        <picture className="absolute inset-0">
          <source
            media="(min-width: 768px)"
            srcSet={transformImage(backgroundImage).desktop}
            type="image/webp"
          />
          <source
            media="(max-width: 767px)"
            srcSet={transformImage(backgroundImage).mobile}
            type="image/webp"
          />
          <img
            src={transformImage(backgroundImage).mobile}
            alt="banner background"
            className="absolute h-full w-full object-cover object-left"
            loading="eager"
            decoding="async"
            {...({ fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
          />
        </picture>
      ) : null}
      <div
        className={tw(
          'isolate m-auto flex w-full items-center justify-center p-[10px] md:w-auto md:justify-between xl:mx-[8.33%]',
          wrapperClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}

const getWordpressBannerType = (siteData: SiteDataQuery['siteData'] | undefined) => {
  if (!siteData) {
    return null
  }

  const wpBannerEntry = Object.entries(siteData).find(
    ([key, value]) =>
      key.endsWith('Banner') && value && typeof value === 'object' && 'type' in value && value.type,
  )

  const wpBanner = wpBannerEntry?.[1]

  if (wpBanner && typeof wpBanner === 'object' && 'type' in wpBanner) {
    return wpBanner.type
  }

  return null
}
